import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  Link,
  Paper,
  TextField,
  CircularProgress
} from '@mui/material';
import { Upload as UploadIcon, Download as DownloadIcon } from '@mui/icons-material';
import InfoIcon from '@mui/icons-material/Info';
import { format, parse, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTechnicians } from '../../contexts/TechniciansContext';
import { useAbsences } from '../../contexts/AbsencesContext';
// TODO: Migrer vers les contextes API
// import { absencesDB, techniciansDB } from '../../db/database';
// import db from '../../db/database';
import { ABSENCE_TYPES } from '../../constants/absenceTypes';

// Mapping des labels vers les codes
const TYPE_MAPPING = {
  'CA': 'CP',
  'JTL': 'JTL',
  'ARRÊT MALADIE': 'MALADIE',
  'FORMATION': 'FORMATION',
  'AUTRE': 'AUTRE'
};

const AbsenceImportExport = ({ onSuccess }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef();
  
  // CORRIGÉ: Déplacer les hooks au niveau du composant
  const { technicians } = useTechnicians();
  const { addAbsence } = useAbsences();

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      // Lire le fichier avec l'encodage UTF-8
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(e);
        reader.readAsText(file, 'UTF-8');
      });

      // Nettoyer les lignes et supprimer les points-virgules supplémentaires dans les commentaires
      const allLines = text.split('\n').map(row => {
        const trimmed = row.trim();
        if (trimmed.startsWith('#')) {
          // Pour les lignes de commentaire, ne garder que le texte avant les points-virgules supplémentaires
          return trimmed.split(';')[0];
        }
        return trimmed;
      });

      // Trouver l'en-tête réel
      const headerIndex = allLines.findIndex(line => 
        line.toLowerCase().includes('nom;prenom;date_debut;date_fin;type;commentaire')
      );

      if (headerIndex === -1) {
        throw new Error('Format de fichier invalide : en-tête non trouvé');
      }

      // Ne traiter que les lignes après l'en-tête
      const rows = allLines.slice(headerIndex + 1).filter(row => row && !row.startsWith('#'));
      
      // Vérifier l'en-tête
      const headerColumns = allLines[headerIndex].toLowerCase().split(';').map(col => col.trim());
      const requiredColumns = ['nom', 'prenom', 'date_debut', 'date_fin', 'type', 'commentaire'];
      
      const missingColumns = requiredColumns.filter(col => !headerColumns.includes(col));
      if (missingColumns.length > 0) {
        throw new Error(`Colonnes manquantes : ${missingColumns.join(', ')}`);
      }

      // Traiter les données
      const absences = [];
      const errors = [];

      for (let i = 0; i < rows.length; i++) {
        const columns = rows[i].split(';').map(col => col.trim());
        if (columns.length !== headerColumns.length) continue;

        const nom = columns[headerColumns.indexOf('nom')];
        const prenom = columns[headerColumns.indexOf('prenom')];
        
        // Trouver le technicien correspondant
        const technician = technicians.find(t => 
          t.lastName.toLowerCase() === nom.toLowerCase() && 
          t.firstName.toLowerCase() === prenom.toLowerCase()
        );

        if (!technician) {
          errors.push(`Ligne ${i + headerIndex + 2}: Technicien non trouvé - ${prenom} ${nom}`);
          continue;
        }

        // Convertir et valider les dates
        const dateDebut = columns[headerColumns.indexOf('date_debut')];
        const dateFin = columns[headerColumns.indexOf('date_fin')];

        let startDate, endDate;
        try {
          // Essayer d'abord le format DD/MM/YYYY
          startDate = parse(dateDebut, 'dd/MM/yyyy', new Date());
          endDate = parse(dateFin, 'dd/MM/yyyy', new Date());

          if (!isValid(startDate) || !isValid(endDate)) {
            // Si ça ne marche pas, essayer le format DD-MM-YYYY
            startDate = parse(dateDebut, 'dd-MM-yyyy', new Date());
            endDate = parse(dateFin, 'dd-MM-yyyy', new Date());

            if (!isValid(startDate) || !isValid(endDate)) {
              throw new Error('Date invalide');
            }
          }

          startDate = format(startDate, 'yyyy-MM-dd');
          endDate = format(endDate, 'yyyy-MM-dd');
        } catch (error) {
          errors.push(`Ligne ${i + headerIndex + 2}: Format de date invalide - Les dates doivent être au format DD/MM/YYYY ou DD-MM-YYYY`);
          continue;
        }

        // Valider le type d'absence
        const typeLabel = columns[headerColumns.indexOf('type')].toUpperCase();
        const type = TYPE_MAPPING[typeLabel];

        if (!type || !Object.keys(ABSENCE_TYPES).includes(type)) {
          errors.push(`Ligne ${i + headerIndex + 2}: Type d'absence invalide "${typeLabel}" - Types valides : ${Object.values(ABSENCE_TYPES).map(t => t.label).join(', ')}`);
          continue;
        }

        const absence = {
          technicianId: technician.id,
          startDate,
          endDate,
          type,
          comment: columns[headerColumns.indexOf('commentaire')]
        };

        absences.push(absence);
      }

      if (errors.length > 0) {
        throw new Error('Erreurs lors de l\'import :\n' + errors.join('\n'));
      }

      // Sauvegarder les absences
      for (const absence of absences) {
        await addAbsence(absence);
        console.log('Absence créée:', absence);
      }

      setOpenDialog(false);
      if (onSuccess) onSuccess();
      event.target.value = '';
    } catch (error) {
      setError(error.message);
    }
  };

  const generateExampleCSV = () => {
    const legend = `# Guide d'importation des absences
# ----------------------------------
#
# Types d'absence disponibles :
${Object.values(ABSENCE_TYPES).map(type => `# - ${type.label}`).join('\n')}
#
# Format des dates : DD/MM/YYYY (exemple: 01/03/2024)
#
# Conseils :
# - Vérifiez que les noms et prénoms correspondent exactement aux techniciens enregistrés
# - Les dates doivent être au format DD/MM/YYYY
# - Le commentaire est optionnel mais recommandé pour le suivi
# - Les lignes commençant par # sont ignorées (commentaires)
#`;
    const header = 'nom;prenom;date_debut;date_fin;type;commentaire';
    const examples = [
      'DUPONT;Jean;01/03/2024;05/03/2024;CA;Congés de printemps',
      'MARTIN;Marie;15/03/2024;15/03/2024;JTL;',
      'DUBOIS;Pierre;01/04/2024;02/04/2024;Formation;Formation React'
    ].join('\n');
    
    const csvContent = `${legend}\n${header}\n${examples}`;
    
    // Créer un Blob avec BOM UTF-8
    const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const blob = new Blob([BOM, csvContent], { type: 'text/csv;charset=utf-8' });
    
    // Créer un lien temporaire pour le téléchargement
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'exemple_import_absences.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Importer CSV
        </Button>
        <Tooltip title="Télécharger un exemple de fichier CSV">
          <IconButton onClick={generateExampleCSV} size="small">
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Importer des absences</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" gutterBottom>
              Importez un fichier CSV contenant la liste des absences à ajouter.
            </Typography>

            <Paper sx={{ p: 2, my: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                Format du fichier :
              </Typography>
              <Typography variant="body2" component="div">
                Le fichier doit contenir les colonnes suivantes :
                <ul>
                  <li><strong>nom</strong> : Nom du technicien</li>
                  <li><strong>prenom</strong> : Prénom du technicien</li>
                  <li><strong>date_debut</strong> : Date de début (DD/MM/YYYY)</li>
                  <li><strong>date_fin</strong> : Date de fin (DD/MM/YYYY)</li>
                  <li><strong>type</strong> : Type d'absence (voir types disponibles ci-dessous)</li>
                  <li><strong>commentaire</strong> : Commentaire (optionnel)</li>
                </ul>
              </Typography>

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Types d'absence disponibles :
              </Typography>
              <Typography variant="body2" component="div">
                <ul>
                  {Object.values(ABSENCE_TYPES).map((value, index) => (
                    <li key={index}>
                      <strong>{value.label}</strong>
                    </li>
                  ))}
                </ul>
              </Typography>

              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Conseils :
              </Typography>
              <Typography variant="body2" component="div">
                <ul>
                  <li>Vérifiez que les noms et prénoms correspondent exactement aux techniciens enregistrés</li>
                  <li>Les dates doivent être au format DD/MM/YYYY (exemple: 01/03/2024)</li>
                  <li>Le commentaire est optionnel mais recommandé pour le suivi</li>
                </ul>
              </Typography>
            </Paper>

            <Box sx={{ mt: 2, mb: 2 }}>
              <Link
                component="button"
                variant="body2"
                onClick={generateExampleCSV}
              >
                Télécharger un exemple de fichier CSV
              </Link>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{error}</pre>
              </Alert>
            )}

            <input
              type="file"
              ref={fileInputRef}
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>
            Annuler
          </Button>
          <Button
            variant="contained"
            onClick={() => fileInputRef.current.click()}
          >
            Sélectionner un fichier
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AbsenceImportExport; 